import React, { useState, useEffect, ChangeEvent, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { KTIcon, KTSVG } from '../../../../_metronic/helpers';
import { toast } from 'react-toastify';
import { formatDateFlatpickr, formatDateWithTime, formatDateWithoutTime } from '../../../helper-functions/DateFunction';
import {
  delete_purchase_order,
  get_all_filter_purchase_order,
  purchase_order_status
} from '../api/purchasesApi';
import Flatpickr from 'react-flatpickr';
import { get_all_vendors } from '../../vendors/api/vendorsApi';
import { permissionCheck } from '../../../helper-functions/PermissionCheck';
import { getSettingValue } from '../../../helper-functions/general_settings_helper';

// --- Type Definitions ---
type PurchaseOrder = {
    id: number;
    PO_date: string;
    vendor_id: number | undefined; // Should match API if it's number
    vendor_name: string | undefined; // Changed from number, name is usually string
    status: string;
    grand_total: number | undefined;
    updated_by_name: string | undefined;
    // updated_by: string | undefined; // This might be ID, updated_by_name is display string
    updated_at: string;
    // Add any other fields your API's 'purchase_orders' array items have
};

interface VendorType { // For filter dropdown
    id: number;
    supplier_name: string | undefined; // Matches your existing type
};

type ApiPaginationInfo = {
    total: number;
    per_page: number;
    current_page: number;
    last_page: number;
    from: number;
    to: number;
};

type ApiPurchaseOrdersDataPayload = {
    purchase_orders: PurchaseOrder[]; // Assuming API returns 'purchase_orders' array
    pagination: ApiPaginationInfo;
    links: { /* ... link structure ... */ };
};

type ApiPurchaseOrdersFullResponse = {
    data: { // From Axios
        success: boolean;
        message: string;
        data: ApiPurchaseOrdersDataPayload; // From your API structure
    };
};
// --- End Type Definitions ---


var purchaseorder_add:boolean, purchaseorder_edit:boolean, purchaseorder_view:boolean, purchaseorder_logs:boolean, purchaseorder_delete:boolean;

function PurchasesList() {
    const [purchaseList, setPurchaseList] = useState<PurchaseOrder[]>([]); // For current page

    // Filter States
    const [fromDateFilter, setFromDateFilter] = useState<string>('');
    const [toDateFilter, setToDateFilter] = useState<string>('');
    const [startingPricefilter, setStartingPriceFilter] = useState<string>('');
    const [endingPriceFilter, setEndingPriceFilter] = useState<string>('');
    const [selectedVendor, setSelectedVendor] = useState<string>(''); // Store as string for select
    const [selectedStatus, setSelectedStatus] = useState<string>('');
    const [searchInput, setSearchInput] = useState<string>('');

    // State for API filter payload
    const [filterPayloadForApi, setFilterPayloadForApi] = useState<any>({
        fromDateFilter: '',
        toDateFilter: '',
        startingPricefilter: '',
        endingPriceFilter: '',
        selectedVendor: '',
        selectedStatus: '',
        // searchTerm: '', // If backend supports search
    });

    // Pagination State
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [itemsPerPage, setItemsPerPage] = useState<number>(10); // Renamed
    const [totalPages, setTotalPages] = useState<number>(0);
    const [totalItems, setTotalItems] = useState<number>(0);
    const [currentRangeFrom, setCurrentRangeFrom] = useState<number>(1);
    const [currentRangeTo, setCurrentRangeTo] = useState<number>(0);
    
    // const [sortBy, setSortBy] = useState<{ key: string, ascending: boolean } | null>(null); // Server-side sorting

    const [selectedPurchaseOrder, setSelectedPurchaseOrder] = useState<PurchaseOrder | null>(null); // Renamed
    const [showAllVendor, setShowAllVendor] = useState<VendorType[]>([]);
    
    const fromDateRef = useRef<Flatpickr>(null);
    const toDateRef = useRef<Flatpickr>(null);
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    purchaseorder_add = permissionCheck('purchaseOrder-create');
    purchaseorder_edit = permissionCheck('purchaseOrder-update');
    purchaseorder_view = permissionCheck('purchaseOrder-view');
    purchaseorder_logs = permissionCheck('purchaseOrder-activity-log');
    purchaseorder_delete = permissionCheck('purchaseOrder-delete');

    // Fetch Vendors for filter dropdown
    const { data: AllVendors } = useQuery('allVendorsForFilter', get_all_vendors, {
        refetchOnWindowFocus: false,
        onSuccess: (response) => {
            // Adjust based on actual response structure of get_all_vendors
            const vendors = response?.data?.data || response?.data; // If nested under .data.data or just .data
            if (vendors && Array.isArray(vendors)) {
                setShowAllVendor(vendors as VendorType[]);
            }
        }
    });


    // --- Main Query for Filtered and Paginated Purchase Orders ---
    const {
        // data: filteredPOQueryData, // Full API response
        isLoading,
        isFetching,
        isError,
        error,
    } = useQuery<ApiPurchaseOrdersFullResponse, Error>(
        ['filtered_purchase_orders', filterPayloadForApi, currentPage, itemsPerPage],
        () => get_all_filter_purchase_order( 
            filterPayloadForApi.fromDateFilter,
            filterPayloadForApi.toDateFilter,
            filterPayloadForApi.startingPricefilter,
            filterPayloadForApi.endingPriceFilter,
            filterPayloadForApi.selectedVendor,
            filterPayloadForApi.selectedStatus,
            currentPage,
            itemsPerPage
            // filterPayloadForApi.searchTerm, // If server-side search
        ),
        {
            keepPreviousData: true,
            refetchOnWindowFocus: false,
            onSuccess: (response) => {
                const apiPayload = response.data.data;
                // Assuming API returns 'purchase_orders' array
                if (apiPayload && apiPayload.purchase_orders && Array.isArray(apiPayload.purchase_orders)) {
                    setPurchaseList(apiPayload.purchase_orders);
                    if (apiPayload.pagination) {
                        setTotalItems(apiPayload.pagination.total);
                        setTotalPages(apiPayload.pagination.last_page);
                        setCurrentPage(apiPayload.pagination.current_page);
                        setCurrentRangeFrom(apiPayload.pagination.from);
                        setCurrentRangeTo(apiPayload.pagination.to);
                    }
                } else {
                    setPurchaseList([]);
                    setTotalItems(0);
                    setTotalPages(0);
                    setCurrentRangeFrom(1);
                    setCurrentRangeTo(0);
                }
            },
            onError: (error: any) => {
                toast.error(error.message || "Failed to fetch purchase orders.", { autoClose: 3000 });
                setPurchaseList([]);
                setTotalItems(0);
                setTotalPages(0);
            },
        }
    );
    
    // Client-side search (optional)
    const displayedPurchaseList = searchInput
        ? purchaseList.filter((po) =>
            po.vendor_name?.toLowerCase().includes(searchInput.toLowerCase()) ||
            po.status?.toLowerCase().includes(searchInput.toLowerCase())
          )
        : purchaseList;

    // Mutations
    const handleDeletePurchaseOrder = useMutation(
        (id: number) => delete_purchase_order(id),
        {
            onSuccess: (response) => {
                toast.success(response.message || "PO deleted successfully.", { autoClose: 3000 });
                queryClient.invalidateQueries(['filtered_purchase_orders', filterPayloadForApi, currentPage, itemsPerPage]);
            },
            onError: (error: any) => {
                const errorMessage = error.response?.data?.message || error.message || "Failed to delete PO.";
                toast.error(errorMessage, { autoClose: 3000 });
            },
        }
    );

    const updatePOStatus = useMutation( // Generic status update mutation
        ({ id, statusFlag }: { id: number; statusFlag: string }) => purchase_order_status(id, statusFlag),
        {
            onSuccess: (response, variables) => {
                toast.success(response.message || `PO status updated to ${variables.statusFlag}.`, { autoClose: 3000 });
                queryClient.invalidateQueries(['filtered_purchase_orders', filterPayloadForApi, currentPage, itemsPerPage]);
            },
            onError: (error: any, variables) => {
                const message = error.response?.data?.message || `Failed to update PO status to ${variables.statusFlag}.`;
                toast.error(message, { autoClose: 3000 });
            },
        }
    );

    const handleStatusRecieptPurchaseOrder = (id: number) => {
        updatePOStatus.mutate({ id, statusFlag: "Receipt-Generated" });
    };
    const handleStatusStockPurchaseOrder = (id: number) => {
        updatePOStatus.mutate({ id, statusFlag: "Stock-Taken" });
    };

  
    const handleApplyFilters = () => {
        setCurrentPage(1);
        setFilterPayloadForApi({
            fromDateFilter: fromDateFilter,
            toDateFilter: toDateFilter,
            startingPricefilter: startingPricefilter,
            endingPriceFilter: endingPriceFilter,
            selectedVendor: selectedVendor,
            selectedStatus: selectedStatus,
            // searchTerm: searchInput, // If server-side search
        });
    };

    const handleClearFilters = () => {
        if (fromDateRef.current) fromDateRef.current.flatpickr.clear();
        if (toDateRef.current) toDateRef.current.flatpickr.clear();
        setFromDateFilter('');
        setToDateFilter('');
        setStartingPriceFilter('');
        setEndingPriceFilter('');
        setSelectedVendor('');
        setSelectedStatus('');
        setSearchInput('');

        setCurrentPage(1);
        setFilterPayloadForApi({
            fromDateFilter: '', toDateFilter: '', startingPricefilter: '', endingPriceFilter: '',
            selectedVendor: '', selectedStatus: '', /* searchTerm: '' */
        });
    };

    // Date picker handlers
    const handleDateFromChange = (selectedDates: Date[]) => {
        if (selectedDates && selectedDates[0]) {
            const formatted = formatDateFlatpickr(selectedDates[0]);
            setFromDateFilter(formatted);
            if (toDateRef.current) toDateRef.current.flatpickr.set('minDate', selectedDates[0]);
        } else {
            setFromDateFilter('');
            if (toDateRef.current) toDateRef.current.flatpickr.set('minDate', null);
        }
    };
    const handleDateToChange = (selectedDates: Date[]) => {
        if (selectedDates && selectedDates[0]) {
            const formatted = formatDateFlatpickr(selectedDates[0]);
            setToDateFilter(formatted);
        } else {
            setToDateFilter('');
        }
    };

    // Pagination handlers
    const itemsPerPageOptions = [10, 20, 30, 50, 100];
    const handleNextPage = () => {
        if (currentPage < totalPages) setCurrentPage(currentPage + 1);
    };
    const handlePreviousPage = () => {
        if (currentPage > 1) setCurrentPage(currentPage - 1);
    };
    const handleItemsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setItemsPerPage(Number(e.target.value));
        setCurrentPage(1);
    };

    // Navigation handlers
    const handlePurchaseLogs = (po: PurchaseOrder) => navigate("/purchase-order/purchase-order-logs", { state: { id: po.id } });
    const handleEditPurchaseOrder = (po: PurchaseOrder) => navigate("/purchase-order/edit-purchase-order", { state: { purchase: po } }); // Check 'purchase' key
    const handleViewPurchaseOrder = (po: PurchaseOrder) => navigate("/purchase-order/view-purchase-order", { state: { id: po.id } });

    // Removed client-side sorting. Server-side sorting needed.

    if (isLoading && !isFetching && currentPage === 1) {
        return <div className='d-flex justify-content-center align-items-center' style={{height: '200px'}}><div className='spinner-border text-primary' role='status'><span className='visually-hidden'>Loading...</span></div></div>;
    }

    return (
        <div className='card card-xxl-stretch mx-4 mb-5 mb-xl-10'>
            {/* Card Header */}
            <div className='card-header border-0 pt-5'>
                <h3 className='card-title align-items-start flex-column'>
                    <span className='card-label fw-bolder fs-3 mb-1'>Purchase Orders List</span>
                     <span className='text-muted mt-1 fw-semibold fs-7'>
                        {totalItems > 0 ? `${totalItems} POs found` : 'No POs'}
                    </span>
                </h3>
                <div className='card-toolbar'>
                    {purchaseorder_add && (
                        <button className='btn btn-sm btn-light-dark' onClick={() => navigate('/purchase-order/add-purchase-order')}>
                            <KTIcon iconName='plus' className='fs-3' /> Add PO
                        </button>
                    )}
                </div>
            </div>

            {/* Filters Section */}
            <div className='card-body pt-0'>
                {/* Row 1: Search, Dates */}
                <div className='row mb-4 align-items-center gy-3'>
                    <div className='col-md-3'>
                        <div className='input-group'>
                            <span className="input-group-text"><KTIcon iconName='magnifier' className='fs-3' /></span>
                            <input
                                type='text'
                                className='form-control form-control-solid'
                                placeholder='Search vendor, status...'
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                            />
                        </div>
                    </div>
                    <div className='col-md-3'>
                        <Flatpickr
                            ref={fromDateRef}
                            onChange={handleDateFromChange}
                            className='form-control form-control-solid'
                            placeholder='From Date (PO Date)'
                            options={{ dateFormat: 'd/m/Y' }}
                        />
                    </div>
                    <div className='col-md-3'>
                        <Flatpickr
                            ref={toDateRef}
                            onChange={handleDateToChange}
                            className='form-control form-control-solid'
                            placeholder='To Date (PO Date)'
                            options={{ dateFormat: 'd/m/Y' }}
                        />
                    </div>
                </div>
                {/* Row 2: Price, Vendor, Status */}
                <div className='row mb-4 align-items-center gy-3'>
                     <div className='col-md-2'>
                        <input
                            type='number'
                            value={startingPricefilter}
                            onChange={(e) => setStartingPriceFilter(e.target.value)}
                            className='form-control form-control-solid'
                            placeholder='Min Price'
                        />
                    </div>
                    <div className='col-md-2'>
                        <input
                            type='number'
                            value={endingPriceFilter}
                            onChange={(e) => setEndingPriceFilter(e.target.value)}
                            className='form-control form-control-solid'
                            placeholder='Max Price'
                        />
                    </div>
                    <div className='col-md-3'>
                        <select
                            className='form-select form-select-solid'
                            value={selectedVendor}
                            onChange={(e) => setSelectedVendor(e.target.value)}
                        >
                            <option value=''>Vendor...</option>
                            {showAllVendor.map((vendor) => (
                                <option key={vendor.id} value={vendor.id}>{vendor.supplier_name}</option>
                            ))}
                        </select>
                    </div>
                    <div className='col-md-3'>
                        <select
                            className='form-select form-select-solid'
                            value={selectedStatus}
                            onChange={(e) => setSelectedStatus(e.target.value)}
                        >
                            <option value=''>Status...</option>
                            <option value='PO-Generated'>PO-Generated</option>
                            <option value='Receipt-Generated'>Receipt-Generated</option>
                            <option value='Stock-Taken'>Stock-Taken</option>
                        </select>
                    </div>
                     <div className='col-md-2 d-flex justify-content-end align-items-center'>
                        <button className='btn btn-light-danger me-2' onClick={handleClearFilters} disabled={isFetching} style={{whiteSpace: 'nowrap'}}>
                            <KTIcon iconName='cross' className='fs-5 me-1' /> Clear
                        </button>
                        <button className='btn btn-primary' onClick={handleApplyFilters} disabled={isFetching} style={{whiteSpace: 'nowrap'}}>
                            <KTIcon iconName='filter-search' className='fs-5 me-1' /> Apply
                        </button>
                    </div>
                </div>
            </div>


            {/* Table Section */}
            <div className='card-body py-3'>
                {isFetching && !isLoading && <div className='text-center text-muted p-3'><i>Updating PO list...</i></div>}
                <div className='table-responsive'>
                    <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
                        <thead>
                            <tr className='fw-bold text-muted bg-light'>
                                <th className='ps-4 min-w-150px rounded-start'>Vendor</th>
                                <th className='min-w-120px'>PO Date</th>
                                <th className='min-w-150px'>Status</th>
                                <th className='min-w-120px'>Grand Total ({getSettingValue('currency_symbol')})</th>
                                <th className='min-w-150px'>Updated By</th>
                                <th className='min-w-150px'>Updated At</th>
                                {(purchaseorder_delete || purchaseorder_edit || purchaseorder_logs || purchaseorder_view) &&
                                    <th className='min-w-150px text-center'>Actions</th>
                                }
                                {(purchaseorder_edit || purchaseorder_add) &&
                                    <th className='min-w-180px text-center rounded-end'>Switch Status</th>
                                }
                            </tr>
                        </thead>
                        <tbody>
                             {isLoading && currentPage === 1 ? (
                                <tr><td colSpan={8} className="text-center py-5"><div className='spinner-border text-primary' /></td></tr>
                            ) : isError ? (
                                <tr><td colSpan={8} className="text-center py-5 text-danger">Error: {error?.message}</td></tr>
                            ) : displayedPurchaseList.length > 0 ? (
                                displayedPurchaseList.map((po) => (
                                    <tr key={po.id}>
                                        <td className='ps-4'>
                                            <span className='text-dark fw-bold text-hover-primary d-block fs-6 cursor-pointer' onClick={() => handleViewPurchaseOrder(po)}>
                                                {po.vendor_name}
                                            </span>
                                        </td>
                                        <td><span className='text-dark fw-semibold d-block fs-6'>{formatDateWithoutTime(po.PO_date)}</span></td>
                                        <td>
                                            <span className={`badge badge-lg ${
                                                po.status === 'PO-Generated' ? 'badge-light-primary' :
                                                po.status === 'Receipt-Generated' ? 'badge-light-info' :
                                                po.status === 'Stock-Taken' ? 'badge-light-success' : 'badge-light-warning'
                                            } fs-7 fw-bold`}>
                                                {po.status}
                                            </span>
                                        </td>
                                        <td><span className='text-dark fw-semibold d-block fs-6'>{po.grand_total}</span></td>
                                        <td><span className='text-dark fw-semibold d-block fs-6'>{po.updated_by_name || 'N/A'}</span></td>
                                        <td><span className='text-dark fw-semibold d-block fs-6'>{formatDateWithTime(po.updated_at)}</span></td>
                                        {(purchaseorder_delete || purchaseorder_edit || purchaseorder_logs || purchaseorder_view) &&
                                        <td className='text-center'>
                                            <div className='d-flex justify-content-center flex-shrink-0'>
                                                {purchaseorder_view && (<button onClick={() => handleViewPurchaseOrder(po)} className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1' title="View"><KTIcon iconName='eye' className='fs-3' /></button>)}
                                                {po.status === "PO-Generated" && purchaseorder_edit && (<button onClick={() => handleEditPurchaseOrder(po)} className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1' title="Edit"><KTIcon iconName='pencil' className='fs-3' /></button>)}
                                                {purchaseorder_logs && (<button onClick={() => handlePurchaseLogs(po)} className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1' title="Logs"><KTIcon iconName='notepad-bookmark' className='fs-3' /></button>)}
                                                {po.status === "PO-Generated" && purchaseorder_delete && (<button onClick={() => setSelectedPurchaseOrder(po)} className='btn btn-icon btn-bg-light btn-active-color-danger btn-sm' title="Delete" data-bs-toggle='modal' data-bs-target='#kt_modal_delete_po'><KTIcon iconName='trash' className='fs-3' /></button>)}
                                            </div>
                                        </td>
                                        }
                                        {(purchaseorder_edit || purchaseorder_add) &&
                                        <td className='text-center'>
                                            {po.status === 'PO-Generated' ? (
                                                <button className='btn btn-sm btn-primary' onClick={() => handleStatusRecieptPurchaseOrder(po.id)} disabled={updatePOStatus.isLoading}>
                                                    Gen. Receipt
                                                </button>
                                            ) : po.status === 'Receipt-Generated' ? (
                                                <button className='btn btn-sm btn-success' onClick={() => handleStatusStockPurchaseOrder(po.id)} disabled={updatePOStatus.isLoading}>
                                                    Stock In
                                                </button>
                                            ) : (
                                                <button className='btn btn-sm btn-secondary disabled'>
                                                    Stock Taken
                                                </button>
                                            )}
                                        </td>
                                        }
                                    </tr>
                                ))
                            ) : (
                                !isFetching && <tr><td colSpan={8} className="text-center py-5">No purchase orders found.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Footer: Pagination and Items Per Page */}
            {totalItems > 0 && (
                <div className='card-footer d-flex justify-content-between align-items-center py-4'>
                    <div>
                        <span className='text-muted me-2'>Showing:</span>
                        <select
                            className='form-select form-select-sm d-inline-block w-auto me-3'
                            value={itemsPerPage}
                            onChange={handleItemsPerPageChange}
                            disabled={isFetching}
                        >
                            {itemsPerPageOptions.map(option => (<option key={option} value={option}>{option}</option>))}
                        </select>
                        <span className='text-muted'>
                            {currentRangeFrom} - {currentRangeTo > totalItems ? totalItems : currentRangeTo} of {totalItems} POs
                        </span>
                    </div>
                
                    <nav>
                        <ul className="pagination pagination-sm mb-0">
                            <li className={`page-item ${currentPage === 1 || isFetching ? 'disabled' : ''}`}>
                                <button className="page-link" onClick={handlePreviousPage} disabled={currentPage === 1 || isFetching}>Previous</button>
                            </li>
                            {/* Optional: Page Number Logic */}
                            <li className={`page-item ${currentPage === totalPages || totalPages === 0 || isFetching ? 'disabled' : ''}`}>
                                <button className="page-link" onClick={handleNextPage} disabled={currentPage === totalPages || totalPages === 0 || isFetching}>Next</button>
                            </li>
                        </ul>
                    </nav>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            <div className="modal fade" tabIndex={-1} id="kt_modal_delete_po">
                <div className="modal-dialog">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">Delete Purchase Order Confirmation</h5>
                            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                        </div>
                        <div className="modal-body">
                             <p>Are you sure you want to delete the PO for: <br/>
                                <strong>{selectedPurchaseOrder?.vendor_name}</strong> (Total: {getSettingValue('currency_symbol')}{selectedPurchaseOrder?.grand_total})?
                            </p>
                            <p className='text-muted small'>This action cannot be undone.</p>
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-light" data-bs-dismiss="modal">Cancel</button>
                            <button
                                type="button"
                                className="btn btn-danger"
                                onClick={() => { if (selectedPurchaseOrder) handleDeletePurchaseOrder.mutate(selectedPurchaseOrder.id); }}
                                data-bs-dismiss="modal"
                                disabled={handleDeletePurchaseOrder.isLoading}
                            >
                                {handleDeletePurchaseOrder.isLoading ? 'Deleting...' : 'Delete PO'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
export default PurchasesList;